import { create } from 'zustand';
import type { QuickAction } from '../types/quickActions';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';

// Default quick actions - in English for internationalization
const DEFAULT_QUICK_ACTIONS: QuickAction[] = [
  { text: 'How it works?' },
  { text: 'Need help' },
  { text: 'Learn more' }
];

interface QuickActionsStore {
  actions: QuickAction[];
  loading: boolean;
  error: Error | null;
  setQuickActions: (actions: QuickAction[], chatbotPublicId?: string) => Promise<boolean>;
  initializeQuickActions: (chatbotPublicId?: string) => Promise<void>;
}

export const useQuickActions = create<QuickActionsStore>((set) => ({
  actions: DEFAULT_QUICK_ACTIONS,
  loading: false,
  error: null,
  
  setQuickActions: async (newActions, chatbotPublicId) => {
    set({ loading: true, error: null });
    
    try {
      if (chatbotPublicId) {
        const success = await updateChatbotConfigSection(
          'quick_actions',
          newActions,
          chatbotPublicId
        );

        if (!success) {
          throw new Error('Failed to update quick actions');
        }
      }
      
      set({ actions: newActions, loading: false });
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error setting quick actions');
      set({ error, loading: false });
      return false;
    }
  },
  
  initializeQuickActions: async (chatbotPublicId) => {
    if (!chatbotPublicId) {
      return;
    }
    
    set({ loading: true, error: null });
    
    try {
      // Get quick actions from the database
      const quickActions = await getChatbotConfigSection<QuickAction[]>(
        'quick_actions',
        chatbotPublicId
      );
      
      // Use retrieved quick actions or defaults
      if (quickActions) {
        set({ 
          actions: quickActions,
          loading: false 
        });
      } else {
        set({ 
          actions: DEFAULT_QUICK_ACTIONS,
          loading: false 
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error initializing quick actions');
      set({ error, loading: false });
    }
  }
}));